import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Box, Chip, Paper, Table, TableBody, TableCell, tableCellClasses, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import PdfPreviewDialog from 'app/components/Dialog/PdfPreviewDialog';
import LoadingPopUp from 'app/components/LoadingPopup';
import { getCustomLocaleDate, getMeetingArchivePdfUrl, highlightText } from 'app/utils/appHelpers';
import { useState } from 'react';
import ArchiveDataTableFilter from './ArchiveDataTableFilter';
import ArchiveDataTableToolbar from './ArchiveDataTableToolbar';

function ArchiveDataTable({ mainData, column }) {
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
        },
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:last-child td, &:last-child th': {
            border: 0,
        },
    }));

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState(mainData);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState("toolbar");
    const [toolbarSearchPayload, setToolbarPayload] = useState({});
    const [selected, setSelected] = useState(null);

    let highlightQuery = '';
    if (searchType === 'toolbar' && toolbarSearchPayload.searchText) {
        highlightQuery = toolbarSearchPayload.searchText;
    }

    const handleRequestSort = (event, property) => {
        const tempOrderBy = property;
        let tempOrder = 'desc';

        if (orderBy === property && order === 'desc') {
            tempOrder = 'asc';
        }

        const tempdata =
            tempOrder === 'desc'
                ? data.sort((a, b) => (b[tempOrderBy] < a[tempOrderBy] ? -1 : 1))
                : data.sort((a, b) => (a[tempOrderBy] < b[tempOrderBy] ? -1 : 1));

        setData(tempdata);
        setOrder(tempOrder);
        setOrderBy(tempOrderBy);
    };

    const tableCellStyle = {};

    const DataTableHead = ({ onRequestSort, customColumnData }) => {
        const createSortHandler = property => event => {
            onRequestSort(event, property);
        };
        return (
            <TableHead>
                <TableRow>
                    {customColumnData.map(column => (
                        <TableCell
                            key={column.id}
                            align={column.align}
                            padding={column.disablePadding ? 'none' : 'normal'}
                            sx={{ whiteSpace: 'normal', ...column.sx }}
                        >
                            <Tooltip
                                title="Sırala"
                                placement={column.align === 'right' ? 'bottom-end' : 'bottom-start'}
                                enterDelay={300}
                            >
                                <TableSortLabel
                                    active={orderBy === column.id}
                                    direction={order}
                                    onClick={createSortHandler(column.id)}
                                    sx={{ whiteSpace: 'normal', '& .MuiTableSortLabel-icon': { flexShrink: 0 } }}
                                >
                                    {column.label}
                                </TableSortLabel>
                            </Tooltip>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
        );
    };

    return (
        <Paper className="col-md-12" sx={{ minWidth: 0, overflow: 'hidden' }}>
            <ArchiveDataTableToolbar
                pageTitle={"Arşiv Arama"}
                setSearchType={setSearchType}
                setData={setData}
                setLoading={setLoading}
                setToolbarPayload={setToolbarPayload}
            />
            <ArchiveDataTableFilter
                setFilterData={(data) => setData(data)}
                setPage={setPage}
                setSelectedFilters={setSelectedFilters}
                setLoading={setLoading}
                setSearchType={setSearchType}
            />
            <LoadingPopUp open={loading} />
            {!loading && (
                <>
                    {Object.keys(selectedFilters).length > 0 && (
                        <div style={{ margin: '10px', display: 'flex', flexWrap: 'wrap' }}>
                            {Object.entries(selectedFilters).map(([key, value]) => {
                                if (value === null || value === "") return null;
                                if (typeof value === "string") {
                                    const stringLabelMapping = {
                                        meetingNo: "Toplantı Sayı"
                                    };
                                    const labelPrefix = stringLabelMapping[key] || key;
                                    return (
                                        <Chip
                                            key={key}
                                            label={`${labelPrefix}: ${value}`}
                                            size="small"
                                            style={{ marginRight: 4, marginBottom: 4 }}
                                        />
                                    );
                                }
                                if (value && value.format) {
                                    const dateLabelMapping = {
                                        pdateStart: "Başlangıç Tarihi",
                                        pdateEnd: "Bitiş Tarihi"
                                    };
                                    const labelPrefix = dateLabelMapping[key] || key;
                                    return (
                                        <Chip
                                            key={key}
                                            label={`${labelPrefix}: ${value.format("DD/MM/YYYY")}`}
                                            size="small"
                                            style={{ marginRight: 4, marginBottom: 4 }}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    )}
                    <div className="flex-auto" style={{ minWidth: 0 }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table
                                size="small"
                                aria-label="customized table"
                                sx={{
                                    minWidth: 960,
                                    '& .MuiTableCell-head': { whiteSpace: 'normal' },
                                    '& .MuiTableSortLabel-root': { whiteSpace: 'normal' },
                                    '& .MuiTableSortLabel-icon': { flexShrink: 0 },
                                }}
                            >
                                <DataTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    rowCount={data.length}
                                    customColumnData={column}
                                />
                                <TableBody>
                                    {data
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((n) => {
                                            const content = n.content ? n.content : '';
                                            const i = highlightQuery
                                                ? content.toLocaleLowerCase().indexOf(highlightQuery.toLocaleLowerCase())
                                                : -1;
                                            const snippet = i < 0
                                                ? `.... ${content.slice(0, 160)} ....`
                                                : `.... ${content.slice(Math.max(0, i - 80), i + highlightQuery.length + 80)} ....`;
                                            return (
                                            <StyledTableRow hover key={"trO" + n.id}>
                                                <StyledTableCell align="left" padding="normal" sx={{ minWidth: 80, maxWidth: 100 }}>
                                                    <Typography variant="caption" style={{ display: 'block', ...tableCellStyle }}>
                                                        {n.inst} {n.unit} / {n.meetingNo}
                                                    </Typography>
                                                </StyledTableCell>
                                                <StyledTableCell align="center" padding="normal" >
                                                    {n.meetingDate ? getCustomLocaleDate('tr', n.meetingDate) : ""}
                                                </StyledTableCell>
                                                <StyledTableCell align="center" padding="normal" >
                                                    {n.pageNumber}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" padding="normal" sx={{ minWidth: 200, maxWidth: 350, whiteSpace: 'pre-line' }}>
                                                    {highlightText(snippet, highlightQuery)}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" padding="none">
                                                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                        <Tooltip title="Arşiv">
                                                            <PictureAsPdfIcon
                                                                color="primary"
                                                                onClick={() => setSelected(n)}
                                                                fontSize="small"
                                                                style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            count={data.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={(e, page) => setPage(page)}
                                            onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
                                            labelRowsPerPage={"Görüntülenecek Satır"}
                                            rowsPerPageOptions={[10, 50, 100, { label: "Tümü", value: -1 }]}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </Box>
                    </div>
                    <PdfPreviewDialog
                        open={Boolean(selected)}
                        onClose={() => setSelected(null)}
                        title={selected ? `${selected.inst} ${selected.unit} / ${selected.meetingNo} — Sayfa ${selected.pageNumber}` : ''}
                        url={getMeetingArchivePdfUrl(selected)}
                        pageNumber={selected ? selected.pageNumber : 1}
                        searchText={highlightQuery}
                    />
                </>
            )}
        </Paper>
    );
}

export default ArchiveDataTable;
